import { useEffect, useRef, useState } from 'react'
import { callsWs, type CallSignal } from '../../ws/callsClient'
import { callsApi } from '../../api/calls'
import { useAuthStore } from '../../stores/authStore'

const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
]

interface Props {
  roomId: string
  callType: 'voice' | 'video'
  title: string
  onClose: () => void
}

// Групповой звонок (mesh): к каждому участнику — отдельный RTCPeerConnection.
export function GroupCallModal({ roomId, callType, title, onClose }: Props) {
  const myId = useAuthStore((s) => s.userId)
  const localStream = useRef<MediaStream | null>(null)
  const localVideoRef = useRef<HTMLVideoElement>(null)
  const peers = useRef<Map<string, RTCPeerConnection>>(new Map())
  const iceServers = useRef<RTCIceServer[]>(ICE_SERVERS)
  const [remote, setRemote] = useState<Record<string, MediaStream>>({})
  const [muted, setMuted] = useState(false)
  const [videoOff, setVideoOff] = useState(callType !== 'video')

  const send = (sig: CallSignal) => callsWs.send(sig)

  useEffect(() => {
    let off: (() => void) | undefined
    let cancelled = false

    const ensurePeer = (peerId: string): RTCPeerConnection => {
      const existing = peers.current.get(peerId)
      if (existing) return existing
      const pc = new RTCPeerConnection({ iceServers: iceServers.current })
      localStream.current?.getTracks().forEach((t) => pc.addTrack(t, localStream.current!))
      pc.onicecandidate = (e) => {
        if (e.candidate) send({ type: 'ice.candidate', room_id: roomId, to: peerId, payload: e.candidate })
      }
      pc.ontrack = (e) => {
        if (e.streams[0]) setRemote((prev) => ({ ...prev, [peerId]: e.streams[0] }))
      }
      pc.onconnectionstatechange = () => {
        if (['failed', 'closed', 'disconnected'].includes(pc.connectionState)) removePeer(peerId)
      }
      peers.current.set(peerId, pc)
      return pc
    }

    const removePeer = (peerId: string) => {
      peers.current.get(peerId)?.close()
      peers.current.delete(peerId)
      setRemote((prev) => {
        const n = { ...prev }
        delete n[peerId]
        return n
      })
    }

    const setup = async () => {
      try {
        localStream.current = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: callType === 'video',
        })
      } catch {
        try {
          localStream.current = await navigator.mediaDevices.getUserMedia({ audio: true })
        } catch {
          onClose()
          return
        }
      }
      if (cancelled) return
      if (localVideoRef.current) localVideoRef.current.srcObject = localStream.current

      try {
        const turn = await callsApi.getTurnCredentials()
        if (turn.urls?.length) {
          iceServers.current = [
            ...ICE_SERVERS,
            ...turn.urls.map((url) => ({ urls: url, username: turn.username, credential: turn.credential })),
          ]
        }
      } catch { /* fallback to STUN */ }

      off = callsWs.on(async (sig: CallSignal) => {
        if (sig.room_id && sig.room_id !== roomId) return
        const from = sig.from || ''
        try {
          switch (sig.type) {
            case 'room.state': {
              const list = (sig.payload as { peers?: string[] })?.peers ?? []
              for (const pid of list) {
                if (pid === myId) continue
                const pc = ensurePeer(pid)
                const offer = await pc.createOffer()
                await pc.setLocalDescription(offer)
                send({ type: 'sdp.offer', room_id: roomId, to: pid, payload: offer })
              }
              break
            }
            case 'sdp.offer': {
              const pc = ensurePeer(from)
              await pc.setRemoteDescription(new RTCSessionDescription(sig.payload as RTCSessionDescriptionInit))
              const answer = await pc.createAnswer()
              await pc.setLocalDescription(answer)
              send({ type: 'sdp.answer', room_id: roomId, to: from, payload: answer })
              break
            }
            case 'sdp.answer': {
              const pc = peers.current.get(from)
              if (pc) await pc.setRemoteDescription(new RTCSessionDescription(sig.payload as RTCSessionDescriptionInit))
              break
            }
            case 'ice.candidate': {
              const pc = peers.current.get(from)
              if (pc && sig.payload) await pc.addIceCandidate(new RTCIceCandidate(sig.payload as RTCIceCandidateInit))
              break
            }
            case 'peer.left': {
              const uid = (sig.payload as { user_id?: string })?.user_id || from
              removePeer(uid)
              break
            }
          }
        } catch { /* ignore transient signaling errors */ }
      })

      send({ type: 'room.join', room_id: roomId })
    }

    setup()
    return () => {
      cancelled = true
      off?.()
      send({ type: 'room.leave', room_id: roomId })
      callsApi.leaveGroupCall(roomId).catch(() => {})
      localStream.current?.getTracks().forEach((t) => t.stop())
      peers.current.forEach((pc) => pc.close())
      peers.current.clear()
    }
  }, [roomId, callType, myId, onClose])

  const toggleMute = () => {
    const next = !muted
    setMuted(next)
    localStream.current?.getAudioTracks().forEach((t) => (t.enabled = !next))
  }
  const toggleVideo = () => {
    const next = !videoOff
    setVideoOff(next)
    localStream.current?.getVideoTracks().forEach((t) => (t.enabled = !next))
  }

  const remoteIds = Object.keys(remote)

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/90">
      <div className="px-5 py-4 text-center">
        <p className="text-white font-semibold text-lg">{title}</p>
        <p className="text-white/60 text-sm">{remoteIds.length + 1} в звонке</p>
      </div>

      <div className="flex-1 overflow-y-auto p-4 grid grid-cols-2 gap-3 content-start">
        {/* Local */}
        <div className="relative rounded-xl overflow-hidden bg-bg-input aspect-video flex items-center justify-center">
          <video ref={localVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
          <span className="absolute bottom-1 left-2 text-white text-xs bg-black/40 px-1.5 rounded">Вы</span>
        </div>
        {remoteIds.map((id) => (
          <RemoteTile key={id} stream={remote[id]} />
        ))}
      </div>

      <div className="flex items-center justify-center gap-4 py-5">
        <button onClick={toggleMute} title={muted ? 'Включить микрофон' : 'Выключить микрофон'}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${muted ? 'bg-white text-black' : 'bg-white/15 text-white hover:bg-white/25'}`}>
          {muted ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="1" y1="1" x2="23" y2="23"/>
              <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/>
              <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/>
              <line x1="12" y1="19" x2="12" y2="23"/>
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
              <line x1="12" y1="19" x2="12" y2="23"/>
            </svg>
          )}
        </button>
        {callType === 'video' && (
          <button onClick={toggleVideo} title={videoOff ? 'Включить камеру' : 'Выключить камеру'}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${videoOff ? 'bg-white text-black' : 'bg-white/15 text-white hover:bg-white/25'}`}>
            {videoOff ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 16v2a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h2m4 0h4a2 2 0 0 1 2 2v2l4-3v9"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="23 7 16 12 23 17 23 7"/>
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
              </svg>
            )}
          </button>
        )}
        <button onClick={onClose} title="Завершить"
          className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white transition-colors">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style={{ transform: 'rotate(135deg)' }}>
            <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/>
          </svg>
        </button>
      </div>
    </div>
  )
}

function RemoteTile({ stream }: { stream: MediaStream }) {
  const ref = useRef<HTMLVideoElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.srcObject = stream
  }, [stream])
  return (
    <div className="relative rounded-xl overflow-hidden bg-bg-input aspect-video flex items-center justify-center">
      <video ref={ref} autoPlay playsInline className="w-full h-full object-cover" />
    </div>
  )
}
